import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/api/client";
import { WA_GREEN, normalizePhone } from "@/utils/whatsapp";
import { useUserWhatsApp } from "./useUserWhatsApp";
import { WhatsAppQRModal } from "./WhatsAppQRModal";

// Sends the current template text as a test message through the user's connected WhatsApp.
export function WaTestSend({ text, templateKey, disabled }: { text: string; templateKey: string; disabled?: boolean }) {
    const { t } = useTranslation();
    const { status } = useUserWhatsApp();
    const [phone, setPhone] = useState("");
    const [busy, setBusy] = useState(false);
    const [qrOpen, setQrOpen] = useState(false);
    const connected = !!status?.connected;

    useEffect(() => { if (status?.phone && !phone) setPhone(`+${status.phone}`); }, [status?.phone, phone]);

    const send = async () => {
        if (!connected) { toast.warning(t("notif.tpl.wa_test_not_connected")); setQrOpen(true); return; }
        if (!normalizePhone(phone)) { toast.error(t("notif.tpl.wa_test_bad_phone")); return; }
        setBusy(true);
        try {
            const r = await api.post("/user-whatsapp/send", {
                to_phone: phone, message: `[TEST] ${text}`.slice(0, 4096), recipient_type: "other", template_key: templateKey,
            });
            toast.success(r.data.mocked ? t("uwa.send.sent_mock") : t("notif.tpl.wa_test_sent", { to: phone }));
        } catch (e: any) {
            const err = e?.response?.data?.detail?.error;
            toast.error(err ? t(err === "whatsapp_not_connected" ? "uwa.send.not_connected" : `uwa.err.${err}` as any, { defaultValue: formatApiError(e) }) : formatApiError(e));
        } finally { setBusy(false); }
    };

    return (
        <>
            {connected && (
                <input data-testid="notif-tpl-wa-test-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel"
                    aria-label={t("notif.tpl.wa_test_phone")} placeholder={t("notif.tpl.wa_test_phone")}
                    className="surface-input px-3 py-2 text-sm w-40" style={{ minHeight: 38 }} />
            )}
            <button type="button" onClick={send} disabled={busy || disabled || !text.trim()} data-testid="notif-tpl-wa-test"
                title={connected ? undefined : t("notif.tpl.wa_test_not_connected")}
                className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm border disabled:opacity-50 transition-colors hover:bg-[var(--input)]"
                style={{ borderColor: connected ? WA_GREEN : "var(--border)", color: "var(--text)" }}>
                {busy ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} style={{ color: WA_GREEN }} />} {t("notif.tpl.wa_test")}
            </button>
            <WhatsAppQRModal open={qrOpen} onClose={() => setQrOpen(false)} />
        </>
    );
}
